// Mẫu « đề xuất → diff → duyệt → áp dụng » (PATTERNS mẫu 1, ADR-001 mục 5): xem trước diff theo id + mức rủi ro + số bản ghi
// THẬT; SAFE áp ngay; REVIEW bấm áp dụng; DANGEROUS tick xác nhận; CRITICAL gõ đúng mã dự án. Server tính lại diff lúc duyệt
// và lúc áp dụng — lệch (ai đó vừa đổi) ⇒ báo và xem lại diff mới.
import type { BlueprintSpec } from "@secretaa/contracts-data";
import { qk } from "@secretaa/sdk-react";
import { ApiError } from "@secretaa/sdk";
import { Button, Card, ConfirmByTyping, DiffPreview, type DiffItem, RiskBadge } from "@secretaa/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { z } from "zod";
import { api } from "../../app/api.ts";
import { useCurrentWorkspace } from "../../app/session.ts";
import { errorMessage } from "../../shared/errors.ts";
import { FormError } from "../../shared/form.tsx";
import { labelOf } from "../data/manifest-view.ts";

type Spec = z.input<typeof BlueprintSpec>;
type Op = { op: string; entity: string; id: string; parentId?: string | undefined; risk: "SAFE" | "REVIEW" | "DANGEROUS" | "CRITICAL"; records: number | null };

/** id (có tiền tố / tmp_*) → nhãn theo ngôn ngữ, gom từ spec hiện tại + spec đề xuất. */
function nameMap(specs: readonly (Spec | null | undefined)[], lang: string) {
  const m = new Map<string, string>();
  for (const s of specs) {
    for (const c of s?.collections ?? []) {
      m.set(c.id, labelOf(c.label, lang));
      for (const f of c.fields ?? []) {
        m.set(f.id, labelOf(f.label, lang));
        if (f.type === "select") for (const o of f.options) m.set(o.id, labelOf(o.label, lang));
      }
    }
    for (const v of s?.views ?? []) m.set(v.id, labelOf(v.label, lang));
    for (const x of s?.scopeTypes ?? []) m.set(x.id, labelOf(x.label, lang));
  }
  return m;
}

export function ChangeReview({ projectId, projectKey, proposal, onDone, onCancel }: { projectId: string; projectKey: string; proposal: Spec; onDone: (version: number) => void; onCancel: () => void }) {
  const { t, i18n } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const qc = useQueryClient();
  const [confirmed, setConfirmed] = useState(false);
  const [typed, setTyped] = useState("");
  const current = useQuery({ queryKey: qk.blueprint(cur.tenant, projectId), queryFn: () => api.blueprint.get(cur.workspaceId, projectId) });
  const preview = useQuery({
    queryKey: qk.blueprintPreview(cur.tenant, projectId, JSON.stringify(proposal)),
    queryFn: () => api.blueprint.preview(cur.workspaceId, projectId, proposal),
    retry: false,
    staleTime: 0,
  });
  const apply = useMutation({
    mutationFn: async () => {
      const p = preview.data!;
      let approvalId: string | undefined;
      if (p.risk !== "SAFE") {
        approvalId = (
          await api.blueprint.approve(cur.workspaceId, projectId, {
            spec: proposal,
            baseVersion: p.baseVersion,
            diffHash: p.diffHash,
            ...(p.risk === "DANGEROUS" ? { confirm: confirmed } : {}),
            ...(p.risk === "CRITICAL" ? { confirmProjectKey: typed } : {}),
          })
        ).approvalId;
      }
      return api.blueprint.apply(cur.workspaceId, projectId, { spec: proposal, diffHash: p.diffHash, ...(approvalId ? { approvalId } : {}) }, p.baseVersion);
    },
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: qk.blueprint(cur.tenant, projectId) });
      await qc.invalidateQueries({ queryKey: qk.blueprintVersions(cur.tenant, projectId) });
      await qc.invalidateQueries({ queryKey: qk.collections(cur.tenant, projectId) });
      onDone(r.version);
    },
    onError: async (e) => {
      // Diff / version đã đổi ⇒ xem lại diff mới (không áp dụng thứ người dùng chưa xem).
      if (e instanceof ApiError && (e.code === "VERSION_CONFLICT" || e.code === "APPROVAL_INVALID")) await preview.refetch();
    },
  });
  if (preview.isPending) return <p>{t("build.diff.loading")}</p>;
  if (preview.isError) return <FormError message={errorMessage(t, preview.error)} />;
  const p = preview.data;
  const names = nameMap([current.data?.spec, proposal], i18n.language);
  const items: DiffItem[] = (p.ops as Op[]).map((o, i) => ({
    key: `${o.op}-${o.id}-${i}`,
    risk: o.risk,
    text: t(`build.op.${o.op}`, { name: names.get(o.id) ?? o.id, parent: o.parentId ? (names.get(o.parentId) ?? "") : "" }),
    records: o.records === null ? null : t("build.diff.records", { count: o.records }),
  }));
  const ready = p.risk === "SAFE" || p.risk === "REVIEW" || (p.risk === "DANGEROUS" && confirmed) || (p.risk === "CRITICAL" && typed === projectKey);
  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">{t("build.diff.title")}</h2>
        <RiskBadge risk={p.risk} label={t(`build.risk.${p.risk.toLowerCase()}`)} />
      </div>
      <DiffPreview title={t("build.diff.title")} items={items} emptyLabel={t("build.diff.empty")} riskLabel={(r) => t(`build.risk.${r.toLowerCase()}`)} />
      {p.dataTouchRecords > 0 ? <p className="text-sm">{t("build.diff.dataTouch", { count: p.dataTouchRecords })}</p> : null}
      {p.risk === "DANGEROUS" ? (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          {t("build.confirm.dangerous")}
        </label>
      ) : null}
      {p.risk === "CRITICAL" ? <ConfirmByTyping expected={projectKey} label={t("build.confirm.critical")} hint={t("build.confirm.criticalHint", { key: projectKey })} onMatchChange={(_, v) => setTyped(v)} /> : null}
      <FormError message={apply.isError ? errorMessage(t, apply.error) : null} />
      <div className="flex gap-2">
        <Button disabled={!ready || apply.isPending || p.ops.length === 0} variant={p.risk === "CRITICAL" ? "danger" : "primary"} onClick={() => apply.mutate()}>
          {t("build.apply")}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </div>
    </Card>
  );
}
